import { useEffect, useMemo } from "react";
import { ThemeProvider } from "styled-components";
import ApplicationProvider from "./Contexts/Application";
import UserProvider from "./Contexts/User";
import ColorModeProvider from "./Contexts/Theme";
import GlobalStyle from "./Pages/Public/styles/GlobalStyle";
import Routes from "./Routes";
import Notifications from "./Wrappers/Notifications";
import Translations from "./Wrappers/Translations";
import {
  DEFAULT_VALUT_SITE_OPTIONS,
  expectsPartnerBranding,
  useGetSiteOptions,
} from "./Hooks/WhiteLabel";
import { BrandingBootstrapLoader } from "./Components/Atoms/BrandingBootstrapLoader";
import { hideAppBootLoader } from "./branding/bootBranding";
import { syncPartnerAppTheme } from "./Pages/Public/partnerAppTheme";
import { FilterProvider } from "./Hooks/useFilter";
import { ThemeProvider as MuiThemeProvider } from "@mui/material";
import { useColorMode } from "./Hooks/useColorMode";
import { createValutMuiTheme } from "./theme/createValutMuiTheme";

const ThemedApp = ({ data }) => {
  const { mode } = useColorMode();
  const primaryColor =
    data?.colors?.primary?.original ??
    DEFAULT_VALUT_SITE_OPTIONS.colors.primary.original;

  const theme = useMemo(
    () =>
      createValutMuiTheme({
        primaryColor,
        mode: mode === "dark" ? "dark" : "light",
      }),
    [primaryColor, mode]
  );

  return (
    <MuiThemeProvider theme={theme}>
      <ThemeProvider theme={data?.colors ?? DEFAULT_VALUT_SITE_OPTIONS.colors}>
        <ApplicationProvider>
          <UserProvider>
            <Translations>
              <Notifications>
                <FilterProvider>
                  <Routes />
                  <GlobalStyle />
                </FilterProvider>
              </Notifications>
            </Translations>
          </UserProvider>
        </ApplicationProvider>
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

const App = () => {
  const { data, brandingReady, brandingError, retryBranding } =
    useGetSiteOptions();
  const siteOptions = data ?? DEFAULT_VALUT_SITE_OPTIONS;

  useEffect(() => {
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
  }, []);

  useEffect(() => {
    if (!brandingReady && !brandingError) return;
    hideAppBootLoader();
    if (brandingReady) {
      syncPartnerAppTheme(siteOptions);
    }
  }, [brandingReady, brandingError, siteOptions]);

  useEffect(() => {
    if (!brandingReady) return;

    const faviconUrl =
      siteOptions.favicon || DEFAULT_VALUT_SITE_OPTIONS.favicon;
    const titleText =
      siteOptions.title || DEFAULT_VALUT_SITE_OPTIONS.title;
    const themeColor =
      siteOptions?.colors?.primary?.dark ||
      DEFAULT_VALUT_SITE_OPTIONS.colors.primary.dark;

    // Never set an empty href — browsers then fall back to a cached /favicon.ico
    // (often the old CRA React atom). Keep local favicon.ico links intact.
    document
      .querySelectorAll(
        'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]'
      )
      .forEach((link) => {
        const href = link.getAttribute("href") || "";
        if (href.includes("favicon.ico")) return;
        link.href = faviconUrl;
      });

    const titleElement = document.getElementById("title");
    if (titleElement) {
      titleElement.textContent = titleText;
    } else {
      document.title = titleText;
    }

    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeMeta) {
      themeMeta = document.createElement("meta");
      themeMeta.setAttribute("name", "theme-color");
      document.head.appendChild(themeMeta);
    }
    themeMeta.setAttribute("content", themeColor);
  }, [
    brandingReady,
    siteOptions.favicon,
    siteOptions.title,
    siteOptions?.colors?.primary?.dark,
  ]);

  if (!brandingReady) {
    return (
      <BrandingBootstrapLoader
        partner={expectsPartnerBranding()}
        error={brandingError}
        onRetry={retryBranding}
      />
    );
  }

  return (
    <ColorModeProvider>
      <ThemedApp data={siteOptions} />
    </ColorModeProvider>
  );
};

export default App;
